import { FormArray, FormBuilder, Validators } from "react-reactive-form";

/**
 * Calculate initial Standard PK block IDs from saved data.
 * Returns a sorted array of block IDs (e.g. [1, 2, 3] or [1, 3] if block 2 was removed).
 */
export const calculateInitialStdBlockIds = wizardForm => {
  // Detect from actual controls first (most reliable after removals)
  const stdColumnControls = Object.keys(wizardForm.controls).filter(key =>
    /^qPkStdColumn(_\d+)?$/.test(key)
  );
  if (stdColumnControls.length > 0) {
    const ids = stdColumnControls.map(key => {
      const match = key.match(/^qPkStdColumn_(\d+)$/);
      return match ? parseInt(match[1]) : 1;
    });
    return [...new Set(ids)].sort((a, b) => a - b);
  }
  // Fallback: check qPkTable controls
  const tableControls = Object.keys(wizardForm.controls).filter(key =>
    /^qPkTable(_\d+)?$/.test(key)
  );
  if (tableControls.length > 0) {
    const ids = tableControls.map(key => {
      const match = key.match(/^qPkTable_(\d+)$/);
      return match ? parseInt(match[1]) : 1;
    });
    return [...new Set(ids)].sort((a, b) => a - b);
  }
  // Fallback: tracking array (sequential on initial load)
  const formValues = wizardForm.value;
  if (formValues.qPkStdAnalysis && Array.isArray(formValues.qPkStdAnalysis)) {
    const len = Math.max(1, formValues.qPkStdAnalysis.length);
    return Array.from({ length: len }, (_, i) => i + 1);
  }
  return [1];
};

/**
 * Add a new Standard PK block with the given block ID.
 */
export const addStdPkBlock = (wizardForm, blockId) => {
  const suffix = blockId > 1 ? `_${blockId}` : "";

  const controlsToEnsure = [
    {
      name: `qPkTable${suffix}`,
      factory: () => FormBuilder.control("", Validators.required),
    },
    {
      name: `qPkPopulation${suffix}`,
      factory: () => FormBuilder.control(""),
    },
    {
      name: `qPkMultiDose${suffix}`,
      factory: () => FormBuilder.control("false"),
    },
    {
      name: `qPkFigures${suffix}`,
      factory: () => FormBuilder.control([], Validators.required),
    },
    {
      name: `qPkFiguresDescription${suffix}`,
      factory: () => FormBuilder.control(""),
    },
    {
      name: `qPkFiguresTimepoints${suffix}`,
      factory: () => new FormArray([]),
    },
    {
      name: `qPkFiguresExtra${suffix}`,
      factory: () => FormBuilder.control([], Validators.required),
    },
    { name: `qPkTimelines${suffix}`, factory: () => new FormArray([]) },
    { name: `qPkStdColumn${suffix}`, factory: () => new FormArray([]) },
  ];

  controlsToEnsure.forEach(({ name, factory }) => {
    if (!wizardForm.contains(name)) {
      wizardForm.addControl(name, factory());
    } else if (
      wizardForm.get(name) instanceof FormArray &&
      wizardForm.get(name).length > 0
    ) {
      while (wizardForm.get(name).length > 0) {
        wizardForm.get(name).removeAt(0);
      }
    }
  });
};

/**
 * Duplicate an existing Standard PK block into a new block ID.
 * Creates the new block's controls (same shape as addStdPkBlock) and
 * pre-populates them with the source block's current values. The duplicated
 * block is fully independent from the source — mutations to one do not
 * affect the other.
 */
export const duplicateStdPkBlock = (wizardForm, sourceBlockId, newBlockId) => {
  addStdPkBlock(wizardForm, newBlockId);

  const sourceSuffix = sourceBlockId > 1 ? `_${sourceBlockId}` : "";
  const newSuffix = newBlockId > 1 ? `_${newBlockId}` : "";

  const scalarControls = [
    "qPkTable",
    "qPkPopulation",
    "qPkMultiDose",
    "qPkFigures",
    "qPkFiguresDescription",
    "qPkFiguresExtra",
  ];

  scalarControls.forEach(baseName => {
    const sourceControl = wizardForm.get(`${baseName}${sourceSuffix}`);
    const newControl = wizardForm.get(`${baseName}${newSuffix}`);
    if (!sourceControl || !newControl) return;
    const value = sourceControl.value;
    newControl.setValue(Array.isArray(value) ? [...value] : value);
  });

  const formArrayControls = [
    "qPkTimelines",
    "qPkStdColumn",
    "qPkFiguresTimepoints",
  ];
  formArrayControls.forEach(baseName => {
    const sourceArray = wizardForm.get(`${baseName}${sourceSuffix}`);
    const newArray = wizardForm.get(`${baseName}${newSuffix}`);
    if (!(sourceArray instanceof FormArray)) return;
    if (!(newArray instanceof FormArray)) return;
    while (newArray.length > 0) {
      newArray.removeAt(0);
    }
    sourceArray.controls.forEach((sourceGroup, index) => {
      const sourceValue = sourceGroup.value || {};
      const groupConfig = Object.keys(sourceValue).reduce((acc, key) => {
        acc[key] = [sourceValue[key] ?? ""];
        return acc;
      }, {});
      const newGroup = FormBuilder.group(groupConfig);
      // Match the meta.key convention used by QuestionsAnswersArray so the
      // FormArray renderer (which keys on itemControl.meta.key) does not crash.
      newGroup.meta = { key: Date.now() + index };
      newArray.push(newGroup);
    });
  });

  // qPkStatsAnalysis is added on render by StandardPKQuestions' useEffect, but
  // copy it eagerly so the duplicated block is prefilled before its first paint.
  const sourceStatsName = `qPkStatsAnalysis${sourceSuffix}`;
  const newStatsName = `qPkStatsAnalysis${newSuffix}`;
  if (wizardForm.contains(sourceStatsName)) {
    const sourceValue = wizardForm.get(sourceStatsName).value;
    const copiedValue = Array.isArray(sourceValue)
      ? [...sourceValue]
      : sourceValue;
    if (wizardForm.contains(newStatsName)) {
      wizardForm.get(newStatsName).setValue(copiedValue);
    } else {
      wizardForm.addControl(newStatsName, FormBuilder.control(copiedValue));
    }
  }
};

/**
 * Remove a Standard PK block by its block ID.
 * Simply removes the block's controls — no data shifting needed.
 */
export const removeStdPkBlock = (wizardForm, blockId) => {
  const suffix = blockId > 1 ? `_${blockId}` : "";
  const controlsToRemove = [
    `qPkTable${suffix}`,
    `qPkPopulation${suffix}`,
    `qPkMultiDose${suffix}`,
    `qPkFigures${suffix}`,
    `qPkFiguresDescription${suffix}`,
    `qPkFiguresTimepoints${suffix}`,
    `qPkFiguresExtra${suffix}`,
    `qPkTimelines${suffix}`,
    `qPkStdColumn${suffix}`,
    `qPkStatsAnalysis${suffix}`,
  ];

  controlsToRemove.forEach(name => {
    if (wizardForm.contains(name)) {
      wizardForm.removeControl(name);
    }
  });
};
